/**
 * Page « Mes documents » du dashboard client (/dashboard/documents).
 *
 * Vue de CONSULTATION, tous dossiers confondus : référence projet, type,
 * statut, date, et consultation (ouvrir / télécharger). On ne téléverse PAS
 * ici : un document s'ajoute uniquement depuis le dossier concerné (parcours).
 */
import type { DocumentType } from '@bcl/shared';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  getDownloadUrl,
  listMineDocuments,
  type MineDocument,
} from '@/features/documents/documents-api';

const documentTypeLabels: Record<DocumentType, string> = {
  piece_identite: "Pièce d'identité",
  permis_construire: 'Permis de construire',
  attestation_adressage: "Attestation d'adressage",
  plans_architecte: 'Plans architecte',
  photo_interieur: 'Photos intérieur',
  photo_exterieur: 'Photos extérieur',
  photo_installation: "Photos d'installation",
  plan_masse: 'Plan de masse',
  plans_vrd: 'Plans VRD',
  courrier_operateur: 'Courrier opérateur',
  autre: 'Autre',
};

function statusBadge(status: MineDocument['status']) {
  switch (status) {
    case 'valide':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          Validé
        </Badge>
      );
    case 'refuse':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          Refusé
        </Badge>
      );
    case 'a_completer':
      return (
        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
          À compléter
        </Badge>
      );
    default:
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          En attente
        </Badge>
      );
  }
}

export function DashboardDocumentsPage() {
  const docs = useQuery({
    queryKey: ['documents', 'mine'],
    queryFn: listMineDocuments,
  });

  async function handleOpen(doc: MineDocument) {
    try {
      const url = await getDownloadUrl(doc.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  const list = docs.data ?? [];

  return (
    <DashboardLayout title="Mes documents">
      <Card>
        <CardHeader>
          <CardTitle>Documents (tous dossiers)</CardTitle>
          <CardDescription>
            Tous les justificatifs déposés sur vos dossiers. Pour en ajouter,
            rendez-vous dans le dossier concerné.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {docs.isLoading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : list.length === 0 ? (
            <div className="rounded-md border bg-white p-6 text-center text-sm text-gray-500">
              Aucun document pour le moment.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dossier</TableHead>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Déposé le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      {d.projectReference}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <FileText className="h-4 w-4 text-gray-400" />
                        {d.filename}
                      </span>
                    </TableCell>
                    <TableCell>{documentTypeLabels[d.documentType]}</TableCell>
                    <TableCell>
                      {statusBadge(d.status)}
                      {d.reviewReason && (
                        <p className="mt-1 text-xs text-gray-500">
                          {d.reviewReason}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(d.uploadedAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpen(d)}
                      >
                        Consulter
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
